import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 touch-feedback relative overflow-hidden -webkit-tap-highlight-color-transparent",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline:
          "wl-btn-outline border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        mobile: "h-12 rounded-md px-4 py-3 min-h-[48px] min-w-[48px]",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  touchEffect?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      touchEffect = true,
      children,
      ...props
    },
    ref
  ) => {
    const [isMobile, setIsMobile] = React.useState(false);

    React.useEffect(() => {
      const checkMobile = () => {
        const isMobileDevice =
          /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );
        const isPWA =
          window.matchMedia("(display-mode: standalone)").matches ||
          (window.navigator as any).standalone === true;

        return isMobileDevice || isPWA;
      };

      setIsMobile(checkMobile());
    }, []);

    const adjustedSize = isMobile && !size ? "mobile" : size;

    // For asChild we need to be careful not to add additional children
    if (asChild) {
      return (
        <Slot
          className={cn(
            buttonVariants({ variant, size: adjustedSize, className })
          )}
          ref={ref as any}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    // For regular buttons we can add the ripple effect
    return (
      <button
        className={cn(
          buttonVariants({ variant, size: adjustedSize, className })
        )}
        ref={ref}
        {...props}
      >
        {children}
        {touchEffect && isMobile && <span className="touch-ripple"></span>}
      </button>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };
